import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createLead,
  getAllLeads,
  getLeadById,
  updateLead,
  deleteLead,
} from "../../api";

// Initial state for leads
const initialState = {
  leads: [],
  currentLead: null,
  loading: false,
  error: null,
  success: false,
  message: "",
};

// Async thunks for lead operations
export const createLeadAsync = createAsyncThunk(
  "leads/createLead",
  async (leadData, { rejectWithValue }) => {
    try {
      const response = await createLead(leadData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to create lead");
    }
  }
);

export const getAllLeadsAsync = createAsyncThunk(
  "leads/getAllLeads",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllLeads();
      // Log the response structure to debug
      console.log("API Response:", response);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch leads");
    }
  }
);

export const getLeadByIdAsync = createAsyncThunk(
  "leads/getLeadById",
  async (leadId, { rejectWithValue }) => {
    try {
      const response = await getLeadById(leadId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch lead");
    }
  }
);

export const updateLeadAsync = createAsyncThunk(
  "leads/updateLead",
  async ({ leadId, updateData }, { rejectWithValue }) => {
    try {
      const response = await updateLead(leadId, updateData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update lead");
    }
  }
);

export const deleteLeadAsync = createAsyncThunk(
  "leads/deleteLead",
  async (leadId, { rejectWithValue }) => {
    try {
      const response = await deleteLead(leadId);
      return { leadId, response: response.data };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete lead");
    }
  }
);

// Create the leads slice
const leadSlice = createSlice({
  name: "leads",
  initialState,
  reducers: {
    resetLeadState: (state) => {
      state.success = false;
      state.error = null;
      state.message = "";
    },
    clearCurrentLead: (state) => {
      state.currentLead = null;
    },
    setLeadMessage: (state, action) => {
      state.message = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Create lead cases
    builder
      .addCase(createLeadAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLeadAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        // Handle both possible response structures
        const newLead = action.payload.data || action.payload;
        state.leads = [newLead, ...state.leads];
        state.message = "Lead created successfully";
      })

      .addCase(createLeadAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Get all leads cases
      .addCase(getAllLeadsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllLeadsAsync.fulfilled, (state, action) => {
        state.loading = false;
        // More flexible handling of response structure
        if (action.payload && action.payload.data) {
          // If response is { data: [...] } or { data: { data: [...] } }
          state.leads = Array.isArray(action.payload.data)
            ? action.payload.data
            : action.payload.data.data || [];
        } else if (Array.isArray(action.payload)) {
          // If response is directly an array
          state.leads = action.payload;
        } else {
          state.leads = [];
        }

        // Log the actual state for debugging
        console.log("Stored leads:", state.leads);
      })
      .addCase(getAllLeadsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get lead by ID cases
      .addCase(getLeadByIdAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLeadByIdAsync.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both possible response structures
        state.currentLead = action.payload.data || action.payload;
      })
      .addCase(getLeadByIdAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update lead cases
      .addCase(updateLeadAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLeadAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "Lead updated successfully";

        // Handle both possible response structures
        const updatedLead = action.payload.data || action.payload;

        if (Array.isArray(state.leads)) {
          const index = state.leads.findIndex(
            (lead) => lead._id === updatedLead._id
          );

          if (index !== -1) {
            state.leads[index] = {
              ...state.leads[index],
              ...updatedLead,
            };
          }
        }

        if (state.currentLead && state.currentLead._id === updatedLead._id) {
          state.currentLead = {
            ...state.currentLead,
            ...updatedLead,
          };
        }
      })

      .addCase(updateLeadAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Delete lead cases
      .addCase(deleteLeadAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteLeadAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "Lead deleted successfully";

        // Remove the lead from the leads array
        state.leads = state.leads.filter(
          (lead) => lead._id !== action.payload.leadId
        );

        // Clear current lead if it matches
        if (
          state.currentLead &&
          state.currentLead._id === action.payload.leadId
        ) {
          state.currentLead = null;
        }
      })
      .addCase(deleteLeadAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  },
});

// Export actions and reducer
export const { resetLeadState, clearCurrentLead, setLeadMessage } =
  leadSlice.actions;

// Export selectors - made more robust to handle different state structures
export const selectLeads = (state) => {
  // Check if state.leads exists
  if (!state.leads) return [];

  // Handle various possible structures
  if (Array.isArray(state.leads)) {
    return state.leads;
  } else if (Array.isArray(state.leads.leads)) {
    return state.leads.leads;
  }

  return [];
};

export const selectCurrentLead = (state) =>
  state.leads ? state.leads.currentLead : null;
export const selectLeadLoading = (state) =>
  state.leads ? state.leads.loading : false;
export const selectLeadError = (state) =>
  state.leads ? state.leads.error : null;
export const selectLeadSuccess = (state) =>
  state.leads ? state.leads.success : false;
export const selectLeadMessage = (state) =>
  state.leads ? state.leads.message : "";

export default leadSlice.reducer;
